import React, { useEffect, useState } from 'react';
import { api } from '../../../helpers/api';
import {
  useAllGamesAdmin,
  useDeleteGame,
  useSecurePing,
} from '../../../helpers/useApi';
import { GameState, GameWithDate } from '../../../types/types';
import { PuzzleBuilder } from './PuzzleBuilder/PuzzleBuilder';
import Game from '../../game/Game';
import './AdminPortal.css';

function AdminPortal() {
  const securePingState = useSecurePing(true);
  const allGames = useAllGamesAdmin();
  const deleteGameMutation = useDeleteGame();
  const [gameState, setGameState] = useState<GameState>();

  useEffect(() => {
    const token = api.getToken();
    if (!token) {
      console.error('No token found, redirecting to login');
      window.location.href = '/';
    }
  }, []);

  const handleDelete = async (game: GameWithDate) => {
    if (
      !window.confirm(
        `Are you sure you want to delete the game on ${game.date} - ${game.game.theme}?`,
      )
    )
      return;

    deleteGameMutation.mutate(
      { id: game._id },
      {
        onSuccess: () => {
          allGames.refetch(); // reload the list
        },
        onError: (error) => {
          alert(`Failed to delete: ${(error as Error).message}`);
        },
      },
    );
  };

  return (
    <div className="admin-portal">
      <header className="admin-header">
        <h1>Admin Portal</h1>
        {securePingState.error ? (
          <p style={{ color: 'red' }}>
            {securePingState.error instanceof Error
              ? securePingState.error.message
              : String(securePingState.error)}
          </p>
        ) : (
          <p>{securePingState.data?.message}</p>
        )}
      </header>
      <section className="section">
        <div className="builder-section">
          <div className="builder-col">
            <PuzzleBuilder setGameState={setGameState} gameState={gameState} />
          </div>

          {gameState && (
            <div className="builder-col">
              <Game
                gameState={gameState}
                setGameState={setGameState}
                resultsOnClick={() => {
                  throw new Error('Function not implemented.');
                }}
              />
            </div>
          )}
        </div>
      </section>
      <section className="section">
        <h2>All Games</h2>
        {allGames.error ? (
          <p style={{ color: 'red' }}>
            {allGames.error instanceof Error
              ? allGames.error.message
              : String(allGames.error)}
          </p>
        ) : allGames.data ? (
          allGames.data.length > 0 ? (
            <div className="table-wrapper">
              <table className="game-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>ID</th>
                    <th>Theme</th>
                    <th>Words</th>
                  </tr>
                </thead>
                <tbody>
                  {allGames.data.map((game) => (
                    <GameRow
                      key={game.date}
                      game={game}
                      onDelete={handleDelete}
                      isDeleting={
                        deleteGameMutation.isPending &&
                        deleteGameMutation.variables?.id === game._id
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No games found.</p>
          )
        ) : (
          <p>Loading games...</p>
        )}
      </section>
    </div>
  );
}

function GameRow({
  game,
  onDelete,
  isDeleting,
}: {
  game: GameWithDate;
  onDelete: (game: GameWithDate) => void;
  isDeleting: boolean;
}) {
  return (
    <tr>
      <td>{game.date}</td>
      <td>{game._id}</td>
      <td>{game.game.theme}</td>
      <td>
        {game.game.words.map((word, index) => (
          <span key={index} className="word-pill">
            {word.text}
          </span>
        ))}
      </td>
      <td>
        <button
          disabled={isDeleting}
          onClick={() => onDelete(game)}
          style={{
            background: '#d9534f',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '4px 8px',
            cursor: 'pointer',
          }}
        >
          {isDeleting ? 'Deleting…' : 'Delete'}
        </button>
      </td>
    </tr>
  );
}

export default AdminPortal;
